/**
 * === STOCK WEBSERVICE INTEGRATION ===
 * Système prioritaire qui charge les statuts de stock réels depuis le webservice
 * S'exécute en premier avant tous les autres scripts
 */

console.log('🚀 [STOCK-WEBSERVICE] Chargement prioritaire du système de stock en temps réel');

(function() {
  'use strict';
  
  // Configuration du webservice
  const STOCK_CONFIG = {
    apiUrl: 'https://stock.stkmn.tech/stck.php',
    debug: true,
    timeout: 10000, // 10 secondes
    retryAttempts: 3,
    retryDelay: 2000 // 2 secondes
  };
  
  // Cache global des données de stock
  window.realTimeStockData = null;
  window.stockDataLoaded = false;
  
  // Fonction de log spécifique au webservice
  function logStock(message, data = null) {
    if (STOCK_CONFIG.debug) {
      const timestamp = new Date().toLocaleTimeString();
      console.log(`[${timestamp}] [STOCK-WS]`, message, data || '');
    }
  }
  
  // Fonction pour obtenir le SKU produit
  function getProductSku() {
    // Chercher le champ hidden product-sku
    const skuInput = document.querySelector('input[name="product-sku"]');
    if (skuInput && skuInput.value) {
      return skuInput.value.trim();
    }
    
    // Fallback : chercher dans d'autres endroits possibles
    const skuElements = document.querySelectorAll('[data-sku], .sku, #sku');
    for (let element of skuElements) {
      const sku = element.textContent || element.value || element.dataset.sku;
      if (sku && sku.trim()) {
        return sku.trim();
      }
    }
    
    return null;
  }
  
  // Fonction pour mapper les statuts en couleurs
  function getStatusColor(state) {
    const stateNormalized = state.toLowerCase().trim();
    
    if (stateNormalized.includes('en stock')) {
      return '#22c55e'; // Vert
    } else if (stateNormalized.includes('stock faible')) {
      return '#f59e0b'; // Orange
    } else if (stateNormalized.includes('rupture')) {
      return '#ef4444'; // Rouge
    }
    
    return '#6b7280'; // Gris par défaut
  }
  
  // Fonction pour interroger le webservice
  async function fetchStockData(sku, retryCount = 0) {
    try {
      logStock(`Interrogation du webservice pour SKU: ${sku} (tentative ${retryCount + 1})`);
      
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), STOCK_CONFIG.timeout);
      
      const response = await fetch(`${STOCK_CONFIG.apiUrl}?reference=${encodeURIComponent(sku)}`, {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json'
        },
        signal: controller.signal
      });
      
      clearTimeout(timeoutId);
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      
      if (!data.success || !data.data || !data.data.inventory || !data.data.inventory.warehouses) {
        throw new Error('Format de réponse invalide du webservice');
      }
      
      logStock(`✅ Données de stock récupérées: ${data.data.inventory.warehouses.length} entrepôts`);
      return data.data;
      
    } catch (error) {
      logStock(`❌ Erreur lors de l'interrogation du webservice:`, error.message);
      
      if (retryCount < STOCK_CONFIG.retryAttempts - 1) {
        logStock(`🔄 Nouvelle tentative dans ${STOCK_CONFIG.retryDelay}ms`);
        await new Promise(resolve => setTimeout(resolve, STOCK_CONFIG.retryDelay));
        return fetchStockData(sku, retryCount + 1);
      }
      
      throw error;
    }
  }
  
  // Fonction pour normaliser les noms de magasins
  function normalizeStoreName(name) {
    return name.toLowerCase()
      .replace(/stokomani\s+/gi, '')
      .replace(/[^a-z0-9\s]/gi, '')
      .trim();
  }
  
  // Fonction pour mettre à jour les données de stock globales
  function processStockData(stockData) {
    const processedData = new Map();
    
    if (stockData.inventory && stockData.inventory.warehouses) {
      stockData.inventory.warehouses.forEach(warehouse => {
        // Ignorer l'entrepôt "Default"
        if (warehouse.name === 'Default') return;
        
        const normalizedName = normalizeStoreName(warehouse.name);
        processedData.set(normalizedName, {
          originalName: warehouse.name,
          normalizedName: normalizedName,
          quantity: warehouse.quantity,
          state: warehouse.state,
          color: getStatusColor(warehouse.state),
          location_id: warehouse.location_id,
          address: warehouse.address
        });
      });
    }
    
    logStock(`📊 Données traitées pour ${processedData.size} magasins`);
    return processedData;
  }
  
  // Fonction pour mettre à jour le statut d'un magasin spécifique
  function updateStoreStatus(storeName, stockInfo) {
    // Chercher dans tous les drawers possibles
    const selectors = [
      `li[data-store-name="${storeName}"]`,
      `li[data-store-name*="${storeName}"]`,
      `.store-item[data-name="${storeName}"]`,
      `.shop-item[data-name="${storeName}"]`
    ];
    
    selectors.forEach(selector => {
      const storeElements = document.querySelectorAll(selector);
      storeElements.forEach(storeElement => {
        updateSingleStoreElement(storeElement, stockInfo);
      });
    });
  }
  
  // Fonction pour mettre à jour un élément de magasin
  function updateSingleStoreElement(storeElement, stockInfo) {
    // Chercher le conteneur de statut existant
    const statusContainer = storeElement.querySelector('.store-status');
    if (!statusContainer) {
      return; // Pas de conteneur de statut, ne rien faire
    }
    
    // Chercher l'indicateur de statut existant (.status-indicator)
    let statusIndicator = statusContainer.querySelector('.status-indicator');
    if (!statusIndicator) {
      // Créer l'indicateur s'il n'existe pas
      statusIndicator = document.createElement('span');
      statusIndicator.className = 'status-indicator';
      statusContainer.insertBefore(statusIndicator, statusContainer.firstChild);
    }
    
    // Mettre à jour la classe CSS de l'indicateur selon le statut
    statusIndicator.className = 'status-indicator';
    if (stockInfo.state.toLowerCase().includes('en stock')) {
      statusIndicator.classList.add('status-in-stock');
      statusIndicator.style.backgroundColor = '#22c55e'; // Vert
    } else if (stockInfo.state.toLowerCase().includes('stock faible')) {
      statusIndicator.classList.add('status-low-stock');
      statusIndicator.style.backgroundColor = '#f59e0b'; // Orange
    } else {
      statusIndicator.classList.add('status-out-of-stock');
      statusIndicator.style.backgroundColor = '#ef4444'; // Rouge
    }
    
    // Chercher l'élément de texte (dernier span)
    let textElement = statusContainer.querySelector('span:last-child');
    if (!textElement || textElement === statusIndicator) {
      // Créer l'élément de texte s'il n'existe pas
      textElement = document.createElement('span');
      statusContainer.appendChild(textElement);
    }
    
    // Supprimer les anciens indicateurs en double
    const oldStockIndicator = textElement.querySelector('.stock-indicator');
    if (oldStockIndicator) {
      oldStockIndicator.remove();
    }
    
    const oldQuantity = textElement.querySelector('.stock-quantity');
    if (oldQuantity) {
      oldQuantity.remove();
    }
    
         // Mettre à jour le contenu du texte sans quantité
     textElement.textContent = stockInfo.state;
     
     // Marquer comme mis à jour par le webservice
     statusContainer.setAttribute('data-stock-source', 'webservice');
     
     logStock(`✅ Élément de magasin mis à jour: ${stockInfo.originalName} -> ${stockInfo.state}`);
  }
  

  
  // Fonction pour mettre à jour tous les magasins affichés
  function updateAllDisplayedStores() {
    if (!window.realTimeStockData) {
      logStock('⚠️ Aucune donnée de stock disponible pour la mise à jour');
      return;
    }
    
    logStock('🔄 Mise à jour de tous les magasins affichés');
    
    // Chercher tous les magasins dans les différents drawers
    const storeSelectors = [
      'li[data-store-name]',
      '.store-item[data-name]',
      '.shop-item[data-name]',
      '.shops-list__item[data-store-name]'
    ];
    
    storeSelectors.forEach(selector => {
      const storeElements = document.querySelectorAll(selector);
      storeElements.forEach(storeElement => {
        const storeName = storeElement.dataset.storeName || storeElement.dataset.name;
        if (storeName) {
          const normalizedStoreName = normalizeStoreName(storeName);
          const stockInfo = window.realTimeStockData.get(normalizedStoreName);
          
          if (stockInfo) {
            updateSingleStoreElement(storeElement, stockInfo);
            logStock(`✅ Magasin mis à jour: ${storeName} -> ${stockInfo.state}`);
          } else {
            logStock(`⚠️ Aucune donnée de stock trouvée pour: ${storeName} (normalisé: ${normalizedStoreName})`);
          }
        }
      });
    });
  }
  
  // Fonction principale d'initialisation
  async function initializeStockSystem() {
    try {
      logStock('🚀 Initialisation du système de stock en temps réel');
      
      const sku = getProductSku();
      if (!sku) {
        logStock('❌ SKU produit non trouvé sur la page');
        return;
      }
      
      logStock(`📦 SKU détecté: ${sku}`);
      
      const stockData = await fetchStockData(sku);
      window.realTimeStockData = processStockData(stockData);
      window.stockDataLoaded = true;
      
      logStock('✅ Système de stock initialisé avec succès');
      
      // Mettre à jour immédiatement si des magasins sont déjà affichés
      updateAllDisplayedStores();
      
      // Observer les changements dans le DOM pour mettre à jour les nouveaux magasins
      setupStockObserver();
      
      // Exposer une fonction globale pour forcer la mise à jour
      window.updateStockDisplay = updateAllDisplayedStores;
      
      // Déclencher un événement pour notifier que les données sont prêtes
      document.dispatchEvent(new CustomEvent('stockDataReady', {
        detail: { stockData: window.realTimeStockData }
      }));
      
    } catch (error) {
      logStock('❌ Erreur lors de l\'initialisation du système de stock:', error.message);
      window.stockDataLoaded = false;
    }
  }
  
  // Observer pour détecter l'ajout de nouveaux magasins dans le DOM
  function setupStockObserver() {
    const observer = new MutationObserver(function(mutations) {
      let shouldUpdate = false;
      
      mutations.forEach(function(mutation) {
        mutation.addedNodes.forEach(function(node) {
          if (node.nodeType === Node.ELEMENT_NODE) {
            // Vérifier si de nouveaux magasins ont été ajoutés
            if (node.querySelector && (
                node.querySelector('li[data-store-name]') ||
                node.querySelector('.store-item') ||
                node.querySelector('.shop-item') ||
                node.classList.contains('store-item') ||
                node.classList.contains('shop-item')
            )) {
              shouldUpdate = true;
            }
          }
        });
      });
      
      if (shouldUpdate) {
        logStock('🔄 Nouveaux magasins détectés, mise à jour des statuts');
        setTimeout(updateAllDisplayedStores, 500);
      }
    });
    
    observer.observe(document.body, {
      childList: true,
      subtree: true
    });
    
    logStock('👁️ Observer de stock configuré');
  }
  
  // Fonction pour obtenir le statut d'un magasin spécifique (utilisée par les autres scripts)
  window.getRealTimeStockStatus = function(storeName) {
    if (!window.realTimeStockData) return null;
    
    const normalizedName = normalizeStoreName(storeName);
    return window.realTimeStockData.get(normalizedName);
  };
  
  // Démarrage automatique du système
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initializeStockSystem);
  } else {
    // Le DOM est déjà chargé
    setTimeout(initializeStockSystem, 100);
  }
  
  logStock('✅ Module de stock en temps réel chargé (prioritaire)');
  
})();

// ========================================
// FIN DU SYSTÈME DE STOCK EN TEMPS RÉEL
// ========================================

/**
 * Pickup Availability Sync - Version Optimisée et Nettoyée
 * Synchronise l'affichage pickup-availability avec le magasin sélectionné
 * ✅ Basé sur la mécanique du header qui fonctionne bien
 * ✅ Plus de conflits ni de boucles infinies
 * ✅ Intégration propre avec StoreFinder Core
 */

console.log('🚀 [PICKUP-SYNC-CLEAN] Script de synchronisation propre chargé');

(function() {
  'use strict';
  
  // Configuration
  const CONFIG = {
    selectors: {
      headerStoreButton: '#store-finder-button-text',
      pickupLocationElement: '.pickup-availability-info__location',
      pickupTimeElement: '.pickup-availability-info__pickup-time',
      pickupPreview: '.pickup-availability-preview'
    },
    debug: true,
    debounceDelay: 300
  };
  
  // État interne
  let isInitialized = false;
  let isUpdating = false;
  let lastKnownShop = null;
  let updateTimeout = null;
  
  // Fonction de log avec timestamp
  function log(message, data = null) {
    if (CONFIG.debug) {
      const timestamp = new Date().toLocaleTimeString();
      console.log(`[${timestamp}] [PICKUP-SYNC-OPT]`, message, data || '');
    }
  }
  
  // Fonction debounced pour éviter les appels multiples
  function debouncedUpdate() {
    if (updateTimeout) {
      clearTimeout(updateTimeout);
    }
    
    updateTimeout = setTimeout(() => {
      updatePickupAvailabilityDisplay();
    }, CONFIG.debounceDelay);
  }
  
  // Fonction pour récupérer la boutique sélectionnée
  function getSelectedStore() {
    // Prioriser StoreFinder si disponible
    if (typeof window.StoreFinder === 'object' && typeof window.StoreFinder.getSelectedShop === 'function') {
      const selectedShop = window.StoreFinder.getSelectedShop();
      if (selectedShop && selectedShop.name) {
        return selectedShop.name;
      }
    }
    
    // Fallback vers localStorage direct
    try {
      const selectedShopJson = localStorage.getItem('selectedShop');
      if (selectedShopJson) {
        const selectedShop = JSON.parse(selectedShopJson);
        if (selectedShop && selectedShop.name) {
          return selectedShop.name;
        }
      }
    } catch (e) {
      log('❌ Erreur lors de la lecture du localStorage:', e);
    }
    
    // Fallback vers le header
    const headerElement = document.querySelector(CONFIG.selectors.headerStoreButton);
    if (headerElement && headerElement.textContent.trim() && headerElement.textContent.trim() !== 'Trouver mon magasin') {
      return headerElement.textContent.trim();
    }
    
    return null;
  }
  
  // Fonction pour charger les données du drawer si nécessaire
  async function ensureDrawerDataLoaded() {
    return new Promise((resolve) => {
      const drawer = document.querySelector('#PopupModal-PickupAvailability-pickup-availability');
      
      if (drawer) {
        const storeItems = drawer.querySelectorAll('li[data-store-name]');
        if (storeItems.length > 0) {
          log('📋 Données du drawer déjà chargées');
          resolve(true);
          return;
        }
      }
      
      // Essayer d'ouvrir le drawer pour charger les données
      const showButton = document.getElementById('ShowPickupAvailabilityDrawer');
      if (showButton) {
        log('🔄 Ouverture du drawer pour charger les données...');
        showButton.click();
        
        // Attendre que les données se chargent
        setTimeout(() => {
          const updatedDrawer = document.querySelector('#PopupModal-PickupAvailability-pickup-availability');
          if (updatedDrawer) {
            const storeItems = updatedDrawer.querySelectorAll('li[data-store-name]');
            if (storeItems.length > 0) {
              log('✅ Données du drawer chargées:', storeItems.length, 'magasins');
              // Fermer le drawer
              const closeBtn = updatedDrawer.querySelector('.drawer__close-btn');
              if (closeBtn) closeBtn.click();
              resolve(true);
              return;
            }
          }
          log('⚠️ Impossible de charger les données du drawer');
          resolve(false);
        }, 1500);
      } else {
        log('❌ Bouton d\'ouverture du drawer non trouvé');
        resolve(false);
      }
    });
  }

  // Fonction pour récupérer le statut du stock depuis le drawer
  function getStockStatusFromDrawer(storeName) {
    // Priorité 1: Utiliser les données du webservice en temps réel si disponibles
    if (window.stockDataLoaded && window.getRealTimeStockStatus) {
      const realTimeStock = window.getRealTimeStockStatus(storeName);
      if (realTimeStock) {
        const isInStock = realTimeStock.state.toLowerCase().includes('en stock');
        return {
          text: realTimeStock.state,
          isInStock: isInStock,
          className: isInStock ? 'status-in-stock' : 'status-out-of-stock',
          color: realTimeStock.color,
          quantity: realTimeStock.quantity,
          source: 'webservice'
        };
      }
    }
    
    // Priorité 2: Fallback vers les données du drawer (méthode originale)
    const drawer = document.querySelector('#PopupModal-PickupAvailability-pickup-availability');
    if (!drawer) {
      return null;
    }
    
    const storeItem = drawer.querySelector(`li[data-store-name="${storeName}"]`);
    if (!storeItem) {
      return null;
    }
    
    // Vérifier si cet élément a déjà été mis à jour par le webservice
    const statusContainer = storeItem.querySelector('.store-status');
    if (statusContainer && statusContainer.getAttribute('data-stock-source') === 'webservice') {
      // Ne pas lire depuis le drawer si déjà mis à jour par le webservice
      return null;
    }
    
    const statusElement = statusContainer ? statusContainer.querySelector('span:last-child') : null;
    if (!statusElement) {
      return null;
    }
    
    const statusText = statusElement.textContent.trim();
    const isInStock = statusText.includes('En stock');
    
    return {
      text: statusText,
      isInStock: isInStock,
      className: isInStock ? 'status-in-stock' : 'status-out-of-stock',
      source: 'drawer'
    };
  }
  
  // Fonction principale de mise à jour
  async function updatePickupAvailabilityDisplay() {
    // Éviter les mises à jour simultanées
    if (isUpdating) {
      log('⏸️ Mise à jour déjà en cours');
      return false;
    }
    
    isUpdating = true;
    log('🔄 Mise à jour de l\'affichage pickup-availability');
    
    try {
      const selectedStore = getSelectedStore();
      const pickupLocationElement = document.querySelector(CONFIG.selectors.pickupLocationElement);
      let pickupTimeElement = document.querySelector(CONFIG.selectors.pickupTimeElement);
      
      // Vérifier que les éléments existent
      if (!pickupLocationElement) {
        log('❌ Élément pickup-availability non trouvé sur cette page');
        return false;
      }
      
      // Vérifier si le magasin a changé
      if (selectedStore === lastKnownShop) {
        log('⏭️ Magasin inchangé, pas de mise à jour nécessaire');
        return true;
      }
      
      lastKnownShop = selectedStore;
      
      if (selectedStore) {
        log('✅ Mise à jour pour la boutique:', selectedStore);
        
        // Récupérer le statut du stock
        let stockStatus = getStockStatusFromDrawer(selectedStore);
        
        // Si pas de statut trouvé, essayer de charger les données
        if (!stockStatus) {
          const dataLoaded = await ensureDrawerDataLoaded();
          if (dataLoaded) {
            stockStatus = getStockStatusFromDrawer(selectedStore);
          }
        }
        
        // Mettre à jour l'affichage de la boutique
        pickupLocationElement.innerHTML = `<span class="color-foreground">${selectedStore.toUpperCase()}</span>`;
        
        // Créer ou mettre à jour l'élément de statut
        if (!pickupTimeElement) {
          pickupTimeElement = document.createElement('p');
          pickupTimeElement.className = 'text-subtext text-sm-extra m-0 pickup-availability-info__pickup-time';
          pickupTimeElement.style.display = 'block';
          pickupTimeElement.style.marginTop = '4px';
          
          // Insérer dans le DOM
          const parentDiv = pickupLocationElement.parentElement;
          const nextElement = pickupLocationElement.nextElementSibling;
          
          // Supprimer l'ancien élément help s'il existe
          const oldHelp = parentDiv.querySelector('.pickup-availability-info__pickup-help');
          if (oldHelp) {
            oldHelp.remove();
          }
          
          if (nextElement && nextElement.tagName.toLowerCase() === 'button') {
            parentDiv.insertBefore(pickupTimeElement, nextElement);
          } else {
            parentDiv.appendChild(pickupTimeElement);
          }
        }
        
        // Afficher le statut du stock
        if (stockStatus && pickupTimeElement) {
          // Supprimer les anciens indicateurs s'ils existent
          const existingIndicators = pickupTimeElement.querySelectorAll('.stock-indicator, .status-indicator');
          existingIndicators.forEach(indicator => indicator.remove());
          
          // Utiliser la couleur du webservice si disponible, sinon les couleurs par défaut
          const backgroundColor = stockStatus.color || (stockStatus.isInStock 
            ? '#22c55e' // Vert
            : '#ef4444' // Rouge
          );
          
          // Créer un indicateur simple sans dupliquer
          const indicator = document.createElement('span');
          indicator.className = 'pickup-status-indicator';
          indicator.style.cssText = `background-color: ${backgroundColor}; width: 8px; height: 8px; border-radius: 50%; display: inline-block; margin-right: 5px; vertical-align: middle;`;
          
          // Construire le contenu proprement
          pickupTimeElement.innerHTML = '';
          pickupTimeElement.appendChild(indicator);
          
          const textNode = document.createTextNode(stockStatus.text);
          pickupTimeElement.appendChild(textNode);
          
          pickupTimeElement.style.display = 'block';
          pickupTimeElement.style.visibility = 'visible';
          
          // Ajouter un indicateur de source pour le debug
          if (stockStatus.source) {
            pickupTimeElement.setAttribute('data-stock-source', stockStatus.source);
          }
          
          log('✅ Statut affiché:', stockStatus.text, `(source: ${stockStatus.source || 'unknown'})`);
        } else if (pickupTimeElement) {
          // Affichage par défaut si pas de statut trouvé
          pickupTimeElement.textContent = 'Vérifier le stock en magasin';
          pickupTimeElement.style.display = 'block';
          pickupTimeElement.style.visibility = 'visible';
          
          log('⚠️ Statut par défaut affiché');
        }
        
      } else {
        // Aucune boutique sélectionnée - affichage par défaut
        log('🔄 Affichage par défaut - aucune boutique sélectionnée');
        
        pickupLocationElement.innerHTML = '<span class="color-foreground">TROUVER MON MAGASIN</span>';
        
        // Restaurer l'élément help
        if (pickupTimeElement && pickupTimeElement.classList.contains('pickup-availability-info__pickup-time')) {
          const helpElement = document.createElement('p');
          helpElement.className = 'text-subtext text-sm-extra m-0 pickup-availability-info__pickup-help';
          helpElement.textContent = 'Vérifier la disponibilité dans d\'autres magasins';
          
          pickupTimeElement.parentElement.replaceChild(helpElement, pickupTimeElement);
        }
      }
      
      return true;
      
    } catch (error) {
      log('❌ Erreur lors de la mise à jour:', error);
      return false;
    } finally {
      // Réinitialiser le flag après un délai pour éviter les conflits
      setTimeout(() => {
        isUpdating = false;
      }, 100);
    }
  }
  
  // Configuration des écouteurs d'événements
  function setupEventListeners() {
    log('👂 Configuration des écouteurs d\'événements');
    
    // Écouter les changements du localStorage
    window.addEventListener('storage', function(e) {
      if (e.key === 'selectedShop') {
        log('🔄 Changement détecté via storage event');
        debouncedUpdate();
      }
    });
    
    // Override localStorage.setItem pour détecter les changements locaux
    const originalSetItem = localStorage.setItem;
    localStorage.setItem = function(key, value) {
      const oldValue = localStorage.getItem(key);
      originalSetItem.call(this, key, value);
      
      if (key === 'selectedShop' && value !== oldValue) {
        log('🔄 Changement détecté via setItem local');
        debouncedUpdate();
      }
    };
    
    // Écouter les changements du header (backup)
    const headerElement = document.querySelector(CONFIG.selectors.headerStoreButton);
    if (headerElement) {
      const observer = new MutationObserver(function() {
        log('🔄 Changement détecté dans le header');
        debouncedUpdate();
      });
      
      observer.observe(headerElement, {
        childList: true,
        subtree: true,
        characterData: true
      });
    }
    
    // Écouter l'événement stockDataReady pour mettre à jour l'affichage
    document.addEventListener('stockDataReady', function(event) {
      log('🔄 Données de stock prêtes, mise à jour de l\'affichage');
      debouncedUpdate();
    });
    
    log('✅ Écouteurs configurés');
  }
  
  // Vérifier la présence de la section pickup-availability
  function checkPickupAvailabilityPresence() {
    const pickupElement = document.querySelector(CONFIG.selectors.pickupPreview);
    return !!pickupElement;
  }
  
  // Observer pour détecter l'apparition dynamique de la section
  function setupDynamicObserver() {
    const observer = new MutationObserver(function(mutations) {
      mutations.forEach(function(mutation) {
        mutation.addedNodes.forEach(function(node) {
          if (node.nodeType === Node.ELEMENT_NODE) {
            if (node.querySelector && 
                (node.querySelector('.pickup-availability-preview') || 
                 node.classList && node.classList.contains('pickup-availability-preview'))) {
              log('✅ Section pickup-availability détectée dynamiquement');
              setTimeout(() => {
                initialize();
              }, 200);
            }
          }
        });
      });
    });
    
    observer.observe(document.body, {
      childList: true,
      subtree: true
    });
  }
  
  // Fonction d'initialisation principale
  function initialize() {
    if (isInitialized) {
      return;
    }
    
    log('🚀 Initialisation de la synchronisation pickup-availability');
    
    // Vérifier la présence de la section
    if (!checkPickupAvailabilityPresence()) {
      log('❌ Section pickup-availability non trouvée');
      return;
    }
    
    isInitialized = true;
    
    // Configurer les écouteurs
    setupEventListeners();
    
    // Mise à jour initiale (avec délai plus long pour attendre le webservice)
    setTimeout(() => {
      updatePickupAvailabilityDisplay();
    }, 1500);
    
    // Mise à jour supplémentaire après que les données du webservice soient chargées
    if (!window.stockDataLoaded) {
      const checkStockData = setInterval(() => {
        if (window.stockDataLoaded) {
          clearInterval(checkStockData);
          log('🔄 Données de stock chargées, mise à jour');
          updatePickupAvailabilityDisplay();
        }
      }, 1000);
      
      // Arrêter de vérifier après 30 secondes maximum
      setTimeout(() => {
        clearInterval(checkStockData);
      }, 30000);
    }
    
    log('✅ Synchronisation pickup-availability initialisée');
  }
  
  // API publique
  window.pickupSync = {
    update: function() {
      log('📞 Mise à jour demandée via API publique');
      debouncedUpdate();
    },
    forceUpdate: function() {
      log('💪 Mise à jour forcée via API publique');
      isUpdating = false;
      lastKnownShop = null;
      updatePickupAvailabilityDisplay();
    },
    updateWithStock: function() {
      log('🔄 Mise à jour avec données de stock forcée');
      if (window.updateStockDisplay) {
        window.updateStockDisplay();
      }
      isUpdating = false;
      lastKnownShop = null;
      updatePickupAvailabilityDisplay();
    },
    getStore: getSelectedStore,
    isPresent: checkPickupAvailabilityPresence,
    getStockData: function() {
      return window.realTimeStockData;
    },
    isStockLoaded: function() {
      return window.stockDataLoaded;
    },
    debug: function() {
      console.log('🔍 Debug pickup-availability-sync:');
      console.log('- Initialized:', isInitialized);
      console.log('- Updating:', isUpdating);
      console.log('- Last known shop:', lastKnownShop);
      console.log('- Current shop:', getSelectedStore());
      console.log('- Pickup element present:', checkPickupAvailabilityPresence());
      console.log('- Stock data loaded:', window.stockDataLoaded);
      console.log('- Stock data size:', window.realTimeStockData ? window.realTimeStockData.size : 0);
      console.log('- Elements:', {
        location: document.querySelector(CONFIG.selectors.pickupLocationElement),
        time: document.querySelector(CONFIG.selectors.pickupTimeElement),
        preview: document.querySelector(CONFIG.selectors.pickupPreview)
      });
    }
  };
  
  // Démarrage automatique
  document.addEventListener('DOMContentLoaded', function() {
    setTimeout(initialize, 1000);
  });
  
  // Observer pour les pages où la section apparaît dynamiquement
  setupDynamicObserver();
  
  // Fallback pour les pages déjà chargées
  if (document.readyState === 'complete') {
    setTimeout(initialize, 1000);
  }
  
  log('✅ Module pickup-availability-sync-optimized chargé');
  
})();

// ========================================
// PICKUP AVAILABILITY DRAWER MANAGEMENT
// Basé sur la mécanique du header qui fonctionne bien
// ========================================

document.addEventListener('DOMContentLoaded', function() {
    // Utiliser la fonction log commune
    function logAction(message, data) {
        window.StoreFinder.logAction('PICKUP-PRODUCT', message, data);
    }

    // Fonction pour masquer le champ de recherche lors de l'initialisation
    function hideSearchFieldOnLoad() {
        setTimeout(function() {
            const userLocation = localStorage.getItem('userLocation');
            const selectedShop = localStorage.getItem('selectedShop');
            
            if (userLocation && userLocation !== 'Aucune') {
                toggleProductSearchField(false);
                logAction("Champ de recherche masqué au chargement car ville existante: " + userLocation);
            } else if (selectedShop) {
                toggleProductSearchField(true);
                logAction("Champ de recherche affiché au chargement - magasin sans ville");
            } else {
                toggleProductSearchField(true);
                logAction("Champ de recherche affiché au chargement - aucune sélection");
            }
        }, 300);
    }

    // Fonction centralisée pour gérer la visibilité du champ de recherche pickup
    function toggleProductSearchField(show) {
        window.StoreFinder.toggleSearchField('#dealer-search-container .search-input-group', show, 'PICKUP-PRODUCT');
    }

    // Attendre que StoreFinder soit prêt
    function waitForStoreFinder() {
        if (typeof window.StoreFinder === 'object' && window.StoreFinder.loadLibraries) {
            window.StoreFinder.loadLibraries(function() {
                initPickupDealerSearch();
            });
        } else {
            setTimeout(waitForStoreFinder, 100);
        }
    }
    
    function initPickupDealerSearch() {
        // Utiliser les warehouses du core commun
        let warehouses = window.StoreFinder.warehouses;
        
        // Variables pour contrôler les tentatives de recherche d'éléments
        let isDrawerOpen = false;
        let autocompleteAttempts = 0;
        let buttonEventsAttempts = 0;
        let chooseButtonsAttempts = 0;
        const MAX_ATTEMPTS = 5;
        
        // Exposer les fonctions au niveau global pour permettre la synchronisation entre drawers
        window.updateStoreInterface = function(shopName) {
            updateShopifyAvailability(shopName);
        };
        
        // Observer l'ouverture du drawer pickup (force l'initialisation périodiquement)
        function setupPickupDrawerObserver() {
            // Essayer plusieurs sélecteurs pour le drawer pickup
            const possibleSelectors = [
                'pickup-availability-drawer',
                '.pickup-availability-drawer',
                '#pickup-availability-drawer',
                '[data-pickup-availability]',
                '.drawer'
            ];
            
            let pickupDrawer = null;
            for (let selector of possibleSelectors) {
                const elements = document.querySelectorAll(selector);
                if (elements.length > 0) {
                    pickupDrawer = elements[0];
                    logAction("Drawer pickup trouvé avec le sélecteur: " + selector);
                    break;
                }
            }
            
            // Forcer l'initialisation périodiquement même sans observer
            const forceInit = function() {
                const searchInput = document.getElementById('dealer-city-search');
                if (searchInput && !searchInput.hasAttribute('data-google-autocomplete-initialized')) {
                    isDrawerOpen = true;
                    autocompleteAttempts = 0;
                    buttonEventsAttempts = 0;
                    chooseButtonsAttempts = 0;
                    
                    logAction("Force initialisation des composants pickup");
                    
                    updateLocationDisplay();
                    hideSearchFieldOnLoad();
                    initAutocomplete();
                    initButtonEvents();
                    initChooseDefaultButtons();
                    
                    const favoriteShopName = getSelectedShopName();
                    updateShopifyAvailability(favoriteShopName);
                    loadSavedShop();
                }
            };
            
            // Vérifier toutes les 2 secondes si l'autocomplétion est initialisée
            setInterval(forceInit, 2000);
            
            // Essayer immédiatement
            setTimeout(forceInit, 500);
            
            if (pickupDrawer) {
                const observer = new MutationObserver(function(mutations) {
                    mutations.forEach(function(mutation) {
                        if (mutation.attributeName === 'hidden') {
                            const isHidden = mutation.target.hidden;
                            isDrawerOpen = !isHidden;
                            
                            if (isDrawerOpen) {
                                setTimeout(forceInit, 500);
                            }
                        }
                    });
                });
                
                observer.observe(pickupDrawer, { attributes: true });
                
                // Vérifier l'état initial
                isDrawerOpen = !pickupDrawer.hidden;
                if (isDrawerOpen) {
                    setTimeout(forceInit, 500);
                }
            }
        }
        
        // Charger les données des boutiques via le core commun
        window.StoreFinder.loadWarehouses(function(data) {
            warehouses = data;
            logAction("Warehouses chargées: " + data.length + " magasins");
            
            loadSavedShop();
            hideSearchFieldOnLoad();
            setupPickupDrawerObserver();
        });
        
        // Utiliser les fonctions communes du core
        function calculateDistance(lat1, lon1, lat2, lon2) {
            return window.StoreFinder.calculateDistance(lat1, lon1, lat2, lon2);
        }
        
        function formatDistance(distance) {
            return window.StoreFinder.formatDistance(distance);
        }
        
        function findNearestShops(lat, lng) {
            return window.StoreFinder.findNearestShops(lat, lng, warehouses);
        }
        
        function saveShopsList(shops) {
            window.StoreFinder.saveShopsList(shops);
        }
        
        // Utiliser la fonction commune pour sauvegarder
        function saveSelectedShop(shop, keepSearchVisible = false) {
            const shopData = window.StoreFinder.saveSelectedShop(shop, keepSearchVisible);
            
            // Mettre à jour l'affichage spécifique au pickup
            updateShopifyAvailability(shop.name);
            displaySelectedShop(shopData, keepSearchVisible);
            
            logAction("Magasin sauvegardé depuis pickup: " + shop.name);
        }

        // Fonction pour initialiser les écouteurs des boutons "Choisir par défaut" du pickup
        function initChooseDefaultButtons() {
            if (!isDrawerOpen) return;
            
            if (chooseButtonsAttempts >= MAX_ATTEMPTS) {
                logAction("Nombre maximum de tentatives atteint pour les boutons");
                return;
            }
            chooseButtonsAttempts++;
            
            const chooseButtons = document.querySelectorAll('pickup-availability-drawer .store-choose-btn');
            if (chooseButtons.length === 0) {
                if (chooseButtonsAttempts < MAX_ATTEMPTS) {
                    setTimeout(initChooseDefaultButtons, 500);
                }
                return;
            }
            
            logAction("Initialisation des écouteurs pickup pour " + chooseButtons.length + " boutons Choisir par défaut");
            
            chooseButtons.forEach(button => {
                // Prévenir les multiples écouteurs
                if (!button.hasAttribute('data-pickup-choose-events-initialized')) {
                    button.setAttribute('data-pickup-choose-events-initialized', 'true');
                    
                    button.addEventListener('click', function(e) {
                        e.preventDefault();
                        const storeName = this.getAttribute('data-store-name');
                        logAction("Clic sur le bouton pickup Choisir par défaut pour " + storeName);
                        
                        let shop = warehouses.find(w => w.name === storeName);
                        
                        // Fallback: créer un objet shop basique depuis le DOM si pas trouvé
                        if (!shop) {
                            shop = createShopFromDOM(storeName);
                        }
                        
                        if (shop) {
                            saveSelectedShop(shop, true);
                            updateUISelection(storeName);
                            logAction("Magasin pickup sélectionné: " + storeName);
                        } else {
                            logAction("Magasin " + storeName + " non trouvé");
                        }
                    });
                }
            });
            
            logAction("Boutons initialisés avec succès");
        }
        
        // Fonction pour créer un objet shop basique depuis les données DOM Shopify
        function createShopFromDOM(storeName) {
            try {
                const storeItem = document.querySelector('.pickup-availability-list__item[data-store-name="' + storeName + '"]');
                if (!storeItem) {
                    logAction("Élément DOM non trouvé pour: " + storeName);
                    return null;
                }
                
                const nameElement = storeItem.querySelector('h3');
                const addressDiv = storeItem.querySelector('.store-address');
                
                if (!nameElement) {
                    logAction("Élément h3 non trouvé pour: " + storeName);
                    return null;
                }
                
                const name = nameElement.firstChild.textContent.trim();
                const addressText = addressDiv ? addressDiv.textContent.trim() : "Adresse non disponible";
                
                // Parser l'adresse
                let address1 = addressText;
                let city = "Ville non disponible";
                let zip = "";
                
                if (addressText && addressText !== "Adresse non disponible") {
                    const lines = addressText.split('\n').filter(line => line.trim());
                    if (lines.length > 0) {
                        address1 = lines[0].trim();
                    }
                    if (lines.length > 1) {
                        const cityZipLine = lines[1].trim();
                        const match = cityZipLine.match(/^(\d{5})\s+(.+)$/);
                        if (match) {
                            zip = match[1];
                            city = match[2];
                        } else {
                            city = cityZipLine;
                        }
                    }
                }
                
                logAction("Shop créé depuis DOM pour: " + name);
                
                return {
                    name: name,
                    id_interne: "dom_" + Date.now(),
                    address: {
                        address1: address1,
                        city: city,
                        zip: zip,
                        coor_1: 0,
                        coor_2: 0
                    }
                };
                
            } catch (error) {
                logAction("Erreur lors de la création du shop depuis le DOM: " + error.message);
                return null;
            }
        }
        
        // Mettre à jour la liste des magasins dans le pickup drawer (Shopify)
        function updateShopifyAvailability(favoriteShopName) {
            const shopifyStoreItems = document.querySelectorAll('.pickup-availability-list__item');
            if (shopifyStoreItems.length === 0) {
                return;
            }

            const parentList = shopifyStoreItems[0].parentNode;
            const storesArray = Array.from(shopifyStoreItems);

            const nearestShopsJson = localStorage.getItem('nearestShops');
            let nearestShopsData = [];
            if (nearestShopsJson) {
                try {
                    nearestShopsData = JSON.parse(nearestShopsJson);
                } catch (e) {
                    console.error("Erreur lors du parsing de nearestShops:", e);
                }
            }
            
            const userLocationValue = localStorage.getItem('userLocation');
            const hasUserLocation = userLocationValue && userLocationValue !== 'Aucune';

            const enrichedStores = storesArray.map(storeElement => {
                const nameElement = storeElement.querySelector('h3');
                const storeName = nameElement ? nameElement.firstChild.textContent.trim() : '';

                let distanceSpan = nameElement ? nameElement.querySelector('.pickup-availability-distance') : null;

                const shopData = nearestShopsData.find(s => s.name && s.name.trim() === storeName);
                let displayDistance = "";
                let distanceKm = Infinity;

                if (hasUserLocation && shopData && shopData.km) {
                    displayDistance = "(" + shopData.km + ")";
                    const match = String(shopData.km).match(/([\d\.]+)\s*(km|m)/);
                    if (match) {
                        distanceKm = parseFloat(match[1]);
                        if (match[2] === 'm') {
                            distanceKm /= 1000;
                        }
                    }
                }

                if (distanceSpan) {
                    distanceSpan.textContent = displayDistance;
                }
                
                return {
                    element: storeElement,
                    name: storeName,
                    distanceKm: distanceKm,
                    isFavorite: storeName === favoriteShopName
                };
            });

            // Tri des magasins: favoris d'abord, puis par distance
            enrichedStores.sort((a, b) => {
                if (a.isFavorite && !b.isFavorite) return -1;
                if (!a.isFavorite && b.isFavorite) return 1;

                if (hasUserLocation && a.distanceKm !== b.distanceKm) {
                    return a.distanceKm - b.distanceKm;
                }
                
                return a.name.localeCompare(b.name);
            });

            // Reconstruire la liste dans le DOM
            while (parentList.firstChild) {
                parentList.removeChild(parentList.firstChild);
            }
            
            enrichedStores.forEach(storeData => {
                parentList.appendChild(storeData.element);
                if (storeData.isFavorite) {
                    storeData.element.classList.add('favorite-store');
                } else {
                    storeData.element.classList.remove('favorite-store');
                }
            });
            
            // Mettre à jour l'interface utilisateur
            if (favoriteShopName) {
                updateUISelection(favoriteShopName);
            }
            
            logAction("Liste Shopify mise à jour - Favori: " + (favoriteShopName || "Aucun") + ", Magasins: " + enrichedStores.length);
        }
        
        // Mettre à jour l'interface utilisateur lors de la sélection
        function updateUISelection(storeName) {
            document.querySelectorAll('.pickup-availability-list__item').forEach(item => {
                const itemName = item.getAttribute('data-store-name');
                const isSelected = itemName === storeName;
                const radioIcon = item.querySelector('.store-radio i');
                
                if (isSelected) {
                    item.classList.add('favorite-store');
                    if (radioIcon) radioIcon.className = 'bi bi-circle-fill';
                } else {
                    item.classList.remove('favorite-store');
                    if (radioIcon) radioIcon.className = 'bi bi-circle';
                }
            });
        }
        
        // Charger la boutique sauvegardée
        function loadSavedShop() {
            const shopData = window.StoreFinder.getSelectedShop();
            if (shopData) {
                updateLocationDisplay();
                displaySelectedShop(shopData, true);
                updateShopifyAvailability(shopData.name);
                logAction("Magasin pickup restauré: " + shopData.name);
            } else {
                logAction("Aucun magasin sauvegardé trouvé");
                updateLocationDisplay();
                updateShopifyAvailability(null);
            }
        }
        
        // Afficher la boutique sélectionnée dans le pickup
        function displaySelectedShop(shopData, keepSearchVisible = false) {
            updateLocationDisplay();
            
            const userLocationValue = localStorage.getItem('userLocation');
            const hasUserLocation = userLocationValue && userLocationValue !== 'Aucune';
            
            const shopNameElement = document.getElementById('dealer-shop-name');
            const shopAddressElement = document.getElementById('dealer-shop-address');
            const selectedShopContainer = document.getElementById('dealer-selected-shop-container');
            
            if (shopNameElement && shopAddressElement && selectedShopContainer) {
                let displayName = shopData.name;
                if (hasUserLocation && shopData.km && shopData.km !== 'Choix par défaut') {
                    displayName += ' (' + shopData.km + ')';
                } else if (!hasUserLocation) {
                    displayName += ' (Choix par défaut)';
                }
                
                shopNameElement.textContent = displayName;
                
                // Essayer de trouver l'adresse dans le DOM
                let storeItem = document.querySelector('.pickup-availability-list__item[data-store-name="' + shopData.name + '"]');
                // Filet de sécurité : si la correspondance exacte échoue (espaces parasites
                // dans les noms selon la source de données), comparer les noms nettoyés
                if (!storeItem) {
                    const targetName = (shopData.name || '').trim();
                    storeItem = Array.from(document.querySelectorAll('.pickup-availability-list__item[data-store-name]'))
                        .find(item => (item.getAttribute('data-store-name') || '').trim() === targetName) || null;
                }
                const addressDiv = storeItem ? storeItem.querySelector('.store-address') : null;
                const address = addressDiv ? addressDiv.textContent.trim() : "Informations non disponibles";
                
                shopAddressElement.textContent = address;
                selectedShopContainer.style.display = 'block';
                
                if (!keepSearchVisible && hasUserLocation) {
                    toggleProductSearchField(false);
                }
                
                logAction("Boutique pickup affichée: " + shopData.name + " - Container visible: " + (selectedShopContainer.style.display === 'block'));
            } else {
                logAction("Éléments DOM manquants pour l'affichage du magasin");
                logAction("shopNameElement: " + !!shopNameElement + ", shopAddressElement: " + !!shopAddressElement + ", selectedShopContainer: " + !!selectedShopContainer);
            }
        }
        
        // Fonction pour obtenir le nom du magasin sélectionné
        function getSelectedShopName() {
            const shopData = window.StoreFinder.getSelectedShop();
            return shopData ? shopData.name : null;
        }
        
        // Fonction pour mettre à jour l'affichage de la localisation pickup
        function updateLocationDisplay() {
            const userLocation = localStorage.getItem('userLocation');
            const standaloneDisplay = document.getElementById('user-location-display-standalone');
            const inShopDisplay = document.getElementById('user-location-display');
            const locationContainer = document.getElementById('user-location-container');
            
            if (userLocation && userLocation !== 'Aucune') {
                if (standaloneDisplay) {
                    standaloneDisplay.textContent = userLocation;
                }
                if (locationContainer) {
                    locationContainer.style.display = 'flex';
                }
                if (inShopDisplay) {
                    inShopDisplay.textContent = userLocation;
                }
            } else {
                if (locationContainer) {
                    locationContainer.style.display = 'none';
                }
                if (inShopDisplay) {
                    inShopDisplay.textContent = 'Aucune';
                }
            }
        }
        
        // Charger l'API Google Places si pas déjà chargée
        function loadGooglePlacesAPI() {
            return new Promise((resolve, reject) => {
                if (typeof google !== 'undefined' && google.maps && google.maps.places) {
                    resolve();
                    return;
                }
                
                if (document.querySelector('script[src*="maps.googleapis.com"]')) {
                    // L'API est en cours de chargement, attendre qu'elle soit prête
                    const checkGoogle = setInterval(() => {
                        if (typeof google !== 'undefined' && google.maps && google.maps.places) {
                            clearInterval(checkGoogle);
                            resolve();
                        }
                    }, 100);
                    return;
                }
                
                const script = document.createElement('script');
                script.src = `https://maps.googleapis.com/maps/api/js?key=AIzaSyA7JBoMVZuoP1Qpta_RoZfWRgl5XjtQSBI&libraries=places&language=fr&region=FR`;
                script.async = true;
                script.defer = true;
                script.onload = resolve;
                script.onerror = reject;
                document.head.appendChild(script);
            });
        }

        // Initialiser l'autocomplétion pickup avec Google Places (identique au header)
        function initAutocomplete() {
            if (autocompleteAttempts >= MAX_ATTEMPTS) {
                logAction("Nombre maximum de tentatives atteint pour l'autocomplétion pickup");
                return;
            }
            autocompleteAttempts++;
            
            const searchInput = document.getElementById('dealer-city-search');
            if (!searchInput) {
                logAction("Champ dealer-city-search non trouvé, tentative " + autocompleteAttempts);
                if (autocompleteAttempts < MAX_ATTEMPTS) {
                    setTimeout(initAutocomplete, 500);
                }
                return;
            }
            
            // Vérifier si l'autocomplétion n'est pas déjà initialisée
            if (searchInput.hasAttribute('data-google-autocomplete-initialized')) {
                logAction("Autocomplétion pickup Google déjà initialisée");
                return;
            }
            
            logAction("Initialisation de l'autocomplétion pickup avec Google Places - Tentative " + autocompleteAttempts);
            
            loadGooglePlacesAPI().then(() => {
                try {
                                         // Créer l'instance d'autocomplétion Google Places (élargi pour codes postaux et arrondissements)
                     const autocomplete = new google.maps.places.Autocomplete(searchInput, {
                         types: ['geocode'], // Permet villes, codes postaux, arrondissements, quartiers
                         componentRestrictions: { country: 'FR' },
                         fields: ['place_id', 'name', 'geometry', 'address_components', 'formatted_address']
                     });
                    
                    // Marquer comme initialisé
                    searchInput.setAttribute('data-google-autocomplete-initialized', 'true');
                    logAction("Google Places Autocomplete créé avec succès pour pickup");
                    
                    // Écouter la sélection d'un lieu (identique au header)
                    autocomplete.addListener('place_changed', function() {
                        logAction("place_changed événement déclenché dans pickup");
                        const place = autocomplete.getPlace();
                        
                        if (!place.geometry || !place.geometry.location) {
                            logAction("Lieu sélectionné sans coordonnées dans pickup:", place);
                            return;
                        }
                        
                        const cityLat = place.geometry.location.lat();
                        const cityLng = place.geometry.location.lng();
                        logAction("Coordonnées obtenues pickup: " + cityLat + ", " + cityLng);
                        
                                                 // Extraire la ville, arrondissement et code postal depuis address_components
                         let cityName = '';
                         let sublocality = ''; // arrondissement ou quartier
                         let postalCode = '';
                         let adminLevel = ''; // département, région
                         
                         if (place.address_components) {
                             place.address_components.forEach(component => {
                                 // Ville principale
                                 if (component.types.includes('locality')) {
                                     cityName = component.long_name;
                                 }
                                 // Arrondissement ou quartier (ex: 17ème Arrondissement)
                                 if (component.types.includes('sublocality_level_1') || component.types.includes('sublocality')) {
                                     sublocality = component.long_name;
                                 }
                                 // Code postal
                                 if (component.types.includes('postal_code')) {
                                     postalCode = component.long_name;
                                 }
                                 // Niveau administratif (fallback pour certaines zones)
                                 if (component.types.includes('administrative_area_level_2')) {
                                     if (!cityName) adminLevel = component.long_name;
                                 }
                             });
                         }
                         
                         // Construire le nom de localisation de façon intelligente
                         let finalLocationName = '';
                         
                         // Cas 1: Arrondissement précis (ex: "17ème Arrondissement, Paris")
                         if (sublocality && cityName) {
                             // Simplifier les noms d'arrondissements parisiens
                             if (sublocality.includes('Arrondissement') && cityName.toLowerCase() === 'paris') {
                                 const arrondNumber = sublocality.match(/(\d+)/);
                                 if (arrondNumber) {
                                     finalLocationName = `Paris ${arrondNumber[1]}`;
                                 } else {
                                     finalLocationName = `${cityName} ${sublocality}`;
                                 }
                             } else {
                                 finalLocationName = `${sublocality}, ${cityName}`;
                             }
                         }
                         // Cas 2: Ville simple
                         else if (cityName) {
                             finalLocationName = cityName;
                         }
                         // Cas 3: Seulement arrondissement/quartier
                         else if (sublocality) {
                             finalLocationName = sublocality;
                         }
                         // Cas 4: Fallback administratif
                         else if (adminLevel) {
                             finalLocationName = adminLevel;
                         }
                         // Cas 5: Utiliser le nom du lieu Google
                         else {
                             finalLocationName = place.name || place.formatted_address?.split(',')[0] || 'Lieu sélectionné';
                         }
                         
                         logAction("Ville extraite pickup: " + finalLocationName + ", Code postal: " + postalCode);
                         
                         // Calculer les distances pour toutes les boutiques (identique au header)
                         const nearestShops = findNearestShops(cityLat, cityLng);
                         logAction("Magasins les plus proches calculés pickup: " + nearestShops.length);
                         
                         // Sauvegarder la localisation sélectionnée avec format intelligent
                         const locationText = postalCode ? `${finalLocationName} (${postalCode})` : finalLocationName;
                        localStorage.setItem('userLocation', locationText);
                        
                        logAction("Ville sélectionnée depuis pickup: " + locationText);
                        
                        // Mettre à jour l'affichage de la localisation
                        updateLocationDisplay();
                        
                        // Sauvegarder la liste pour trier les magasins (identique au header)
                        saveShopsList(nearestShops);
                        
                        // Mettre à jour les distances dans le drawer pickup
                        const favoriteShopName = getSelectedShopName();
                        updateShopifyAvailability(favoriteShopName);
                        
                        // Masquer le champ de recherche (identique au header)
                        toggleProductSearchField(false);
                        
                        // Mettre à jour le magasin sélectionné s'il y en a un (identique au header)
                        let shopJson = localStorage.getItem('selectedShop');
                        if (shopJson) {
                            try {
                                const savedShop = JSON.parse(shopJson);
                                const shop = warehouses.find(w => w.id_interne === savedShop.id_interne);
                                if (shop) {
                                    // Mettre à jour la distance
                                    shop.distance = calculateDistance(
                                        cityLat,
                                        cityLng,
                                        shop.address.coor_1,
                                        shop.address.coor_2
                                    );
                                    // Mettre à jour l'affichage
                                    const updatedShopData = {
                                        name: shop.name,
                                        id_interne: shop.id_interne,
                                        km: formatDistance(shop.distance)
                                    };
                                    localStorage.setItem('selectedShop', JSON.stringify(updatedShopData));
                                    displaySelectedShop(updatedShopData, true);
                                }
                            } catch (e) {
                                logAction("Erreur lors de la mise à jour du magasin pickup sélectionné:", e);
                            }
                        }
                        
                        // Vider le champ de recherche après sélection (identique au header)
                        setTimeout(() => {
                            searchInput.value = '';
                        }, 100);
                    });
                    
                    logAction("Autocomplétion pickup Google Places initialisée avec succès");
                } catch (error) {
                    logAction("Erreur autocomplétion pickup Google: " + error.message);
                    if (autocompleteAttempts < MAX_ATTEMPTS) {
                        setTimeout(initAutocomplete, 1000);
                    }
                }
            }).catch(error => {
                logAction("Erreur chargement Google Places API pickup: " + error.message);
                if (autocompleteAttempts < MAX_ATTEMPTS) {
                    setTimeout(initAutocomplete, 1000);
                }
            });
        }
        
        // Initialiser les événements des boutons pickup (simplifié)
        function initButtonEvents() {
            if (!isDrawerOpen) return;
            
            if (buttonEventsAttempts >= MAX_ATTEMPTS) {
                return;
            }
            buttonEventsAttempts++;
            
            const gpsBtn = document.getElementById('dealer-gps-btn');
            const clearLocationBtn = document.getElementById('clear-location-btn');
            const removeShopBtn = document.getElementById('dealer-remove-shop');
            
            if (!gpsBtn) {
                if (buttonEventsAttempts < MAX_ATTEMPTS) {
                    setTimeout(initButtonEvents, 500);
                }
                return;
            }
            
            logAction("Initialisation des événements des boutons pickup");
            
            // Prévenir les multiples écouteurs
            if (!gpsBtn.hasAttribute('data-pickup-events-initialized')) {
                gpsBtn.setAttribute('data-pickup-events-initialized', 'true');
                
                // Bouton GPS
                gpsBtn.addEventListener('click', function() {
                    logAction("Clic sur le bouton GPS pickup");
                    
                    window.StoreFinder.getUserLocation()
                        .then((result) => {
                            updateLocationDisplay();
                            const favoriteShopName = getSelectedShopName();
                            updateShopifyAvailability(favoriteShopName);
                            toggleProductSearchField(false);
                            logAction("Position GPS pickup obtenue");
                        })
                        .catch((error) => {
                            logAction("Erreur GPS pickup: " + error);
                        });
                });
            }
            
            // Bouton de suppression de localisation
            if (clearLocationBtn && !clearLocationBtn.hasAttribute('data-pickup-clear-events-initialized')) {
                clearLocationBtn.setAttribute('data-pickup-clear-events-initialized', 'true');
                
                clearLocationBtn.addEventListener('click', function() {
                    localStorage.setItem('userLocation', 'Aucune');
                    updateLocationDisplay();
                    
                    document.querySelectorAll('.pickup-availability-distance').forEach(span => {
                        span.textContent = '';
                    });
                    
                    toggleProductSearchField(true);
                    
                    const favoriteShopName = getSelectedShopName();
                    updateShopifyAvailability(favoriteShopName);
                    
                    logAction("Localisation pickup supprimée");
                });
            }
            
            // Bouton de suppression de magasin
            if (removeShopBtn && !removeShopBtn.hasAttribute('data-pickup-remove-events-initialized')) {
                removeShopBtn.setAttribute('data-pickup-remove-events-initialized', 'true');
                
                removeShopBtn.addEventListener('click', function() {
                    window.StoreFinder.removeSelectedShop('PICKUP-PRODUCT');
                    
                    const selectedShopContainer = document.getElementById('dealer-selected-shop-container');
                    if (selectedShopContainer) {
                        selectedShopContainer.style.display = 'none';
                    }
                    
                    toggleProductSearchField(true);
                    
                    document.querySelectorAll('.pickup-availability-list__item').forEach(item => {
                        item.classList.remove('favorite-store');
                    });
                    
                    updateShopifyAvailability(null);
                    
                    logAction("Magasin pickup supprimé et container masqué");
                });
            }
            
            logAction("Événements des boutons pickup initialisés");
        }
    }
    
    // Écouter les changements dans localStorage pour synchronisation avec le header
    window.addEventListener('storage', function(e) {
        if (e.key === 'selectedShop' || e.key === 'userLocation') {
            setTimeout(function() {
                logAction("Événement storage pickup détecté: " + e.key);
                
                const savedShopJson = localStorage.getItem('selectedShop');
                if (savedShopJson) {
                    try {
                        const savedShop = JSON.parse(savedShopJson);
                        displaySelectedShop(savedShop, true);
                        updateShopifyAvailability(savedShop.name);
                        logAction("Pickup synchronisé via storage: " + savedShop.name);
                    } catch (err) {
                        logAction("Erreur synchronisation pickup: " + err.message);
                    }
                } else {
                    const selectedShopContainer = document.getElementById('dealer-selected-shop-container');
                    if (selectedShopContainer) {
                        selectedShopContainer.style.display = 'none';
                    }
                    
                    toggleProductSearchField(true);
                    updateShopifyAvailability(null);
                    logAction("Pickup synchronisé: magasin supprimé");
                }
                
                updateLocationDisplay();
                
                if (typeof window.updateStoreFinderButtonText === 'function') {
                    window.updateStoreFinderButtonText();
                }
            }, 100);
        }
    });
    
    // Démarrer l'initialisation
    waitForStoreFinder();
    
    logAction("Script pickup-availability drawer management initialisé");
});